import React from 'react';
import {
  Typography,
  Card,
  CardTitle,
  CardBody,
  CardActions,
  Button,
  ButtonGroup,
  Alert,
} from '@one-thd/sui-atomic-components';
import { useDrawer } from '../../hooks/use-drawer';
import { getCardImagePath } from '../../utils/credit-cards';
import { InstantCheckoutDrawer } from '../InstantCheckoutDrawer/InstantCheckoutDrawer';
import {
  CHANGE_HOW_YOU_PAY,
  EDIT_CARD_DETAILS,
  SELECT_PAYMENT,
  EDIT_PAYMENT,
} from '../../models/views';
import { IPayment } from '../../models/payments';
import { track } from '../../analytics/analytics';
import { INSTANT_CHECKOUT_OVERLAY } from '../../constants/analytics';

interface IInstantcheckoutPaymentMethodCardProps {
  payment: IPayment | undefined;
  isToggleOn: boolean;
}

export const InstantCheckoutPaymentMethodCard = ({
  payment,
  isToggleOn,
}: IInstantcheckoutPaymentMethodCardProps) => {
  const textColor = isToggleOn ? 'primary' : 'inactive';
  const cardDetailsColor = isToggleOn ? 'subtle' : 'inactive';

  const { openDrawer, hideDrawer } = useDrawer();

  const handleOnEditCardDetails = () => {
    track(INSTANT_CHECKOUT_OVERLAY, {
      value: 'instant checkout edit payment method',
    });
    openDrawer(InstantCheckoutDrawer, {
      hideDrawer,
      initialView: EDIT_PAYMENT,
      flowName: EDIT_CARD_DETAILS,
    });
  };

  const handleOnChangeHowToPay = () => {
    track(INSTANT_CHECKOUT_OVERLAY, {
      value: 'instant checkout select payment method',
    });
    openDrawer(InstantCheckoutDrawer, {
      hideDrawer,
      initialView: SELECT_PAYMENT,
      flowName: CHANGE_HOW_YOU_PAY,
    });
  };

  const missingDetailsAlert = () => {
    return (
      <div className="sui-mt-4">
        <Alert status="error" className="sui-mb-0">
          <Typography>Missing or invalid card details:</Typography>
          <ul className="sui-mt-4 sui-list-disc sui-list-inside">
            {payment?.cardDetails?.isExpired && (
              <Typography component="li">Card expired</Typography>
            )}
            {!payment?.billingAddress?.id && (
              <Typography component="li">No billing address</Typography>
            )}
          </ul>
        </Alert>
      </div>
    );
  };

  return (
    <Card
      className="sui-block sui-col-span-12 md:sui-col-span-4 2xl:sui-col-span-3"
      data-testid="checkout-payment-method"
    >
      <CardTitle
        header={
          <Typography variant="h3" color={textColor} data-qm-mask="">
            {payment?.nickName}
          </Typography>
        }
      />
      {payment && (
        <CardBody>
          <div className="sui-flex sui-flex-wrap sui-items-center">
            {payment?.cardDetails?.cardBrand?.name && (
              <img
                width={50}
                height="auto"
                className={`sui-inline-block sui-mr-4 ${
                  !isToggleOn ? 'sui-grayscale' : ''
                }`}
                src={getCardImagePath(payment?.cardDetails?.cardBrand?.name)}
                alt="Credit card logo"
              />
            )}
            <div className="sui-mr-4">
              <Typography
                variant="body-lg"
                color={cardDetailsColor}
                data-qm-mask=""
              >
                ****{payment?.cardDetails?.cardNumberLast4}
              </Typography>
            </div>
            {payment?.cardDetails?.expirationMonth &&
              payment?.cardDetails?.expirationYear && (
                <Typography
                  variant="body-lg"
                  color={cardDetailsColor}
                  data-qm-mask=""
                >
                  {`Exp ${
                    payment?.cardDetails?.expirationMonth
                  }/${payment?.cardDetails?.expirationYear.slice(-2)}`}
                </Typography>
              )}
            {(payment?.cardDetails?.isExpired ||
              !payment?.billingAddress?.id) &&
              missingDetailsAlert()}
          </div>
        </CardBody>
      )}
      <CardActions>
        <ButtonGroup orientation="vertical" variant="secondary">
          <Button
            disabled={!isToggleOn}
            data-testid="checkout-btn-edit"
            onClick={handleOnEditCardDetails}
          >
            Edit Card Details
          </Button>
          <Button
            disabled={!isToggleOn}
            data-testid="checkout-btn-change"
            onClick={handleOnChangeHowToPay}
          >
            Change How You Pay
          </Button>
        </ButtonGroup>
      </CardActions>
    </Card>
  );
};
